import React, { useState, useEffect, useRef, useMemo } from "react";
import { Empty } from "antd";
import { useSelector } from "react-redux";
import { getApi } from "../../../../api/api_instance";
import { ReactComponent as Print } from "../../../../assets/svgs/prints.svg";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";

// Dashboard Switcher Component
export const DashboardSwitcher = ({
  activeDashboard: controlledActiveDashboard,
  onDashboardChange = () => {},
}) => {
  const [internalActiveDashboard, setInternalActiveDashboard] =
    useState("dummy");
  const [isMobile, setIsMobile] = useState(false);
  const activeDashboard = controlledActiveDashboard ?? internalActiveDashboard;

  const handleDashboardChange = (value) => {
    if (controlledActiveDashboard === undefined) {
      setInternalActiveDashboard(value);
    }
    onDashboardChange(value);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <div className="flex h-6 sm:h-7 border border-[#458276] bg-[#E5F3F6] rounded w-full sm:w-[50%] overflow-hidden">
      <button
        onClick={() => handleDashboardChange("dummy")}
        className={`
          w-[50%] px-1 py-1 text-[10px] sm:px-3 sm:text-sm transition-all duration-200
          ${
            activeDashboard === "dummy"
              ? "bg-[#06513D] text-white font-normal"
              : "bg-[#E5F3F6] text-[#979797] font-normal"
          }
        `}
      >
        {isMobile ? "Dummy" : "Dummy Dashboard"}
      </button>
      <button
        onClick={() => handleDashboardChange("live")}
        className={`
          w-[50%] px-1 py-1 text-[10px] sm:px-3 sm:text-sm transition-all duration-200
          ${
            activeDashboard === "live"
              ? "bg-[#06513D] text-white font-normal"
              : "bg-[#E5F3F6] text-[#979797] font-normal"
          }
        `}
      >
        {isMobile ? "Live" : "Your Dashboard"}
      </button>
    </div>
  );
};

const Dropdown = ({
  items,
  onSelect,
  shorten = false,
  position = "left",
  widthClass = "w-auto sm:w-24 md:w-28 lg:w-24",
}) => {
  const getPositionClass = () => {
    switch (position) {
      case "right":
        return "right-0";
      case "center":
        return "left-1/2 transform -translate-x-1/2";
      case "left":
      default:
        return "left-0";
    }
  };

  return (
    <div
      className={`absolute top-full mt-1 ${getPositionClass()} ${widthClass} bg-white border rounded shadow-lg z-40`}
    >
      {items && items.length > 0 ? (
        items.map((item) => (
          <div
            key={item?.value ?? item?.label ?? item}
            onClick={() => onSelect(item)}
            className="px-3 py-1 text-center text-xs sm:text-sm cursor-pointer hover:bg-[#06513D] hover:text-white transition-colors whitespace-nowrap"
          >
            {shorten
              ? String(item?.label ?? item).slice(0, 3)
              : (item?.label ?? item)}
          </div>
        ))
      ) : (
        <div className="py-2 px-3 flex justify-center items-center">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{ height: 20 }}
            description={
              <span className="text-[10px] text-gray-400">No data</span>
            }
            style={{ margin: 0 }}
          />
        </div>
      )}
    </div>
  );
};

// Breadcrumb Component
export const BreadcrumbNavigation = ({
  items = ["Home", "Emission Tracking"],
}) => {
  return (
    <div className="flex justify-end text-xs font-light px-2 sm:px-4 pb-0 no-print">
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index === items.length - 1 ? (
            <TextStyles color="#06513D">{item}</TextStyles>
          ) : (
            <>
              <p className="text-gray-600">{item}</p>
              <span className="mx-1 text-gray-400">/</span>
            </>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Main Component
export default function TimeFilterBar({
  title = "Emission Tracking",
  showBreadcrumb = true,
  breadcrumbItems = [],
  showBackButton = true,
  onBackClick = () => {},
  className = "",
  activeDashboard,
  onDashboardChange,
  customFilters = [],
  onYearChange,
  selectedYearValue,
  onQuarterChange,
  selectedQuarterValue,
  onMonthChange,
  selectedMonthValue,
}) {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const { user, employeeLogin } = useSelector((state) => state.auth);

  const [hideDummy, setHideDummy] = useState(() => {
    return sessionStorage.getItem("hideDummy") === "true";
  });

  const [checkingStatus, setCheckingStatus] = useState(() => {
    return sessionStorage.getItem("hideDummy") === null;
  });

  const [activeYearType, setActiveYearType] = useState(
    user?.reportingYearType || "financial",
  );

  useEffect(() => {
    if (selectedYearValue !== undefined) {
      setSelectedYear(selectedYearValue);
      if (selectedYearValue) setSelectedPeriod("Yearly");
    }
  }, [selectedYearValue]);

  useEffect(() => {
    if (selectedQuarterValue !== undefined) {
      setSelectedQuarter(selectedQuarterValue);
      if (selectedQuarterValue) setSelectedPeriod("Quarterly");
    }
  }, [selectedQuarterValue]);

  useEffect(() => {
    if (selectedMonthValue !== undefined) {
      setSelectedMonth(selectedMonthValue);
      if (selectedMonthValue) setSelectedPeriod("Monthly");
    }
  }, [selectedMonthValue]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        let apiUrl = "";
        if (employeeLogin && user?._id) {
          apiUrl = `/api/client/project/user-detail?limit=100&skip=0&sort=-createdAt&user_id=${user._id}`;
        } else {
          apiUrl = "/api/client/project/get?limit=100&skip=0&sort=-createdAt";
        }

        const response = await getApi({ apiUrl });
        if (response && response.success && response.data) {
          const completedProject = response.data.find(
            (proj) => proj.status === "Project Setup Completed",
          );
          const hasCompletedProject = !!completedProject;
          sessionStorage.setItem("hideDummy", String(hasCompletedProject));
          setHideDummy(hasCompletedProject);
          if (hasCompletedProject) {
            if (activeDashboard !== "live") {
              onDashboardChange("live");
            }
            if (completedProject.year) {
              setActiveYearType(completedProject.year);
            }
          }
        }
      } catch (error) {
        console.error("Error checking project status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchProjects();
  }, [employeeLogin, user?._id, onDashboardChange, activeDashboard]);

  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [openCustomFilter, setOpenCustomFilter] = useState(null);

  const [showDropdown, setShowDropdown] = useState({
    year: false,
    quarter: false,
    month: false,
  });

  const financialYears = [
    "2026-2027",
    "2025-2026",
    "2024-2025",
    "2023-2024",
    "2022-2023",
    "2021-2022",
  ];
  const calendarYears = ["2026", "2025", "2024", "2023", "2022", "2021"];
  const yearOptions = useMemo(
    () => (activeYearType === "calendar" ? calendarYears : financialYears),
    [activeYearType],
  );
  // Default to yearOptions[0] on first load, only if the parent
  // hasn't already provided/selected a year value.
  useEffect(() => {
    if (!selectedYearValue && yearOptions.length > 0) {
      const defaultYear = yearOptions[0];
      setSelectedYear(defaultYear);
      setSelectedPeriod("Yearly");
      onYearChange?.(defaultYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearOptions]);
  const quarterlyOptions = ["Q1", "Q2", "Q3", "Q4"];
  const monthlyOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    const checkScreen = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
      setIsDesktop(width >= 1024);
    };
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown({ year: false, quarter: false, month: false });
        setOpenCustomFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
    setOpenCustomFilter(null);
    setShowDropdown({
      year: period === "Yearly",
      quarter: period === "Quarterly",
      month: period === "Monthly",
    });
  };

  const handleCustomFilterClick = (key) => {
    setSelectedPeriod(null);
    setShowDropdown({ year: false, quarter: false, month: false });
    setOpenCustomFilter((prev) => (prev === key ? null : key));
  };

  const handlePrint = () => window.print();

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      {showBreadcrumb && <BreadcrumbNavigation items={breadcrumbItems} />}

      <div className="flex flex-col gap-3 sm:gap-4">
        {/* Mobile Layout - Individual Dropdowns */}
        {isMobile && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <h1 className="text-sm font-bold text-[#458276] truncate">
                  {title}
                </h1>
              </div>

              <button
                onClick={handlePrint}
                className="flex-shrink-0 p-1 hover:text-[#458276] text-gray-700 no-print"
                title="Print"
              >
                <Print className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col gap-2 no-print">
              {!checkingStatus && !hideDummy && (
                <DashboardSwitcher
                  activeDashboard={activeDashboard}
                  onDashboardChange={onDashboardChange}
                />
              )}

              {/* Individual Button Containers for Mobile */}
              <div className="flex gap-1">
                {/* Year Button */}
                <div className="relative flex-1">
                  <button
                    onClick={() => handlePeriodClick("Yearly")}
                    className="w-full py-1.5 h-7 text-xs rounded border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition truncate"
                  >
                    {selectedYear || "Year"}
                  </button>
                  {selectedPeriod === "Yearly" && showDropdown.year && (
                    <Dropdown
                      items={yearOptions}
                      onSelect={(val) => {
                        setSelectedYear(val);
                        onYearChange?.(val);
                        setShowDropdown((prev) => ({ ...prev, year: false }));
                      }}
                      position="center"
                    />
                  )}
                </div>

                {/* Quarter Button */}
                <div className="relative flex-1">
                  <button
                    onClick={() => handlePeriodClick("Quarterly")}
                    className="w-full py-1.5 h-7 text-xs rounded border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition truncate"
                  >
                    {selectedQuarter || "Qtr"}
                  </button>
                  {selectedPeriod === "Quarterly" && showDropdown.quarter && (
                    <Dropdown
                      items={quarterlyOptions}
                      onSelect={(val) => {
                        setSelectedQuarter(val);
                        setShowDropdown((prev) => ({
                          ...prev,
                          quarter: false,
                        }));
                      }}
                      position="center"
                    />
                  )}
                </div>

                {/* Month Button */}
                <div className="relative flex-1">
                  <button
                    onClick={() => handlePeriodClick("Monthly")}
                    className="w-full py-1.5 h-7 text-xs rounded border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition truncate"
                  >
                    {selectedMonth || "Mon"}
                  </button>
                  {selectedPeriod === "Monthly" && showDropdown.month && (
                    <Dropdown
                      items={monthlyOptions}
                      onSelect={(val) => {
                        setSelectedMonth(val);
                        setShowDropdown((prev) => ({ ...prev, month: false }));
                      }}
                      shorten={true}
                      position="center"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tablet Layout - Individual Dropdowns */}
        {isTablet && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="text-xl font-bold text-[#06513D]">{title}</div>
            </div>

            <div className="flex justify-between items-center gap-4 no-print">
              {!checkingStatus && !hideDummy && (
                <DashboardSwitcher
                  activeDashboard={activeDashboard}
                  onDashboardChange={onDashboardChange}
                />
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="p-1 hover:text-[#458276] text-gray-700"
                  title="Print"
                >
                  <Print className="w-7 h-7" />
                </button>

                {/* Custom filters for Tablet */}
                <div className="flex gap-2">
                  {customFilters.map((filter) => (
                    <div key={filter.key} className="relative">
                      <button
                        onClick={() => handleCustomFilterClick(filter.key)}
                        className="px-3 py-1 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#06513D] hover:text-white transition whitespace-nowrap min-w-[120px]"
                      >
                        {filter.valueLabel || filter.label}
                      </button>
                      {openCustomFilter === filter.key && (
                        <Dropdown
                          items={
                            filter.options && filter.options.length > 0
                              ? [
                                  { label: "All", value: null },
                                  ...filter.options,
                                ]
                              : []
                          }
                          onSelect={(val) => {
                            filter.onChange?.(val?.value ?? undefined);
                            setOpenCustomFilter(null);
                          }}
                          position="center"
                          widthClass={
                            filter.key === "location" ||
                            filter.key === "department"
                              ? "min-w-[170px] w-max max-h-[220px] overflow-y-auto overflow-x-hidden"
                              : "w-40"
                          }
                        />
                      )}
                    </div>
                  ))}

                  {/* Year Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Yearly")}
                      className="px-3 py-1 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedYear || "Year"}
                    </button>
                    {selectedPeriod === "Yearly" && showDropdown.year && (
                      <Dropdown
                        items={yearOptions}
                        onSelect={(val) => {
                          console.log(
                            "👉 Year selected in TimeFilterBar:",
                            val,
                          );
                          setSelectedYear(val);
                          onYearChange?.(val);
                          setShowDropdown((prev) => ({ ...prev, year: false }));
                        }}
                        position="center"
                      />
                    )}
                  </div>

                  {/* Quarter Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Quarterly")}
                      className="px-3 py-1 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedQuarter || "Quarter"}
                    </button>
                    {selectedPeriod === "Quarterly" && showDropdown.quarter && (
                      <Dropdown
                        items={quarterlyOptions}
                        onSelect={(val) => {
                          setSelectedQuarter(val);
                          setShowDropdown((prev) => ({
                            ...prev,
                            quarter: false,
                          }));
                        }}
                        position="center"
                      />
                    )}
                  </div>

                  {/* Month Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Monthly")}
                      className="px-3 py-1 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#458276] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedMonth || "Month"}
                    </button>
                    {selectedPeriod === "Monthly" && showDropdown.month && (
                      <Dropdown
                        items={monthlyOptions}
                        onSelect={(val) => {
                          setSelectedMonth(val);
                          setShowDropdown((prev) => ({
                            ...prev,
                            month: false,
                          }));
                        }}
                        shorten={true}
                        position="center"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Layout - Individual Dropdowns */}
        {isDesktop && (
          <div className="flex justify-between items-start w-full mt-0">
            <div className="flex items-center gap-1">
              <div className="text-xl lg:text-lg font-bold text-[#06513D] mt-2">
                {title}
              </div>
            </div>

            <div className="flex items-center gap-4 mt-1 no-print">
              <button
                onClick={handlePrint}
                className="p-1 hover:text-[#06513D] text-gray-700"
                title="Print"
              >
                <Print className="w-7 h-7 lg:w-8 lg:h-8 text-[#458276]" />
              </button>

              {!checkingStatus && !hideDummy && (
                <DashboardSwitcher
                  activeDashboard={activeDashboard}
                  onDashboardChange={onDashboardChange}
                />
              )}

              {/* Custom filters before period controls */}
              <div className="flex gap-2">
                {customFilters.map((filter) => (
                  <div key={filter.key} className="relative">
                    <button
                      onClick={() => handleCustomFilterClick(filter.key)}
                      className="px-3 py-0.5 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#06513D] hover:text-white transition whitespace-nowrap min-w-[120px]"
                    >
                      {filter.valueLabel || filter.label}
                    </button>
                    {openCustomFilter === filter.key && (
                      <Dropdown
                        items={
                          filter.options && filter.options.length > 0
                            ? [{ label: "All", value: null }, ...filter.options]
                            : []
                        }
                        onSelect={(val) => {
                          filter.onChange?.(val?.value ?? undefined);
                          setOpenCustomFilter(null);
                        }}
                        position="center"
                        widthClass={
                          filter.key === "location" ||
                          filter.key === "department"
                            ? "min-w-[170px] w-max max-h-[220px] overflow-y-auto overflow-x-hidden"
                            : "w-44"
                        }
                      />
                    )}
                  </div>
                ))}

                <div className="flex gap-2">
                  {/* Year Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Yearly")}
                      className="px-3 py-0.5 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#06513D] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedYear || "Year"}
                    </button>
                    {selectedPeriod === "Yearly" && showDropdown.year && (
                      <Dropdown
                        items={yearOptions}
                        onSelect={(val) => {
                          console.log(
                            "👉 Year selected in TimeFilterBar:",
                            val,
                          );
                          setSelectedYear(val);
                          onYearChange?.(val);
                          setShowDropdown((prev) => ({ ...prev, year: false }));
                        }}
                        position="center"
                      />
                    )}
                  </div>

                  {/* Quarter Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Quarterly")}
                      className="px-3 py-0.5 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#06513D] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedQuarter || "Quarter"}
                    </button>
                    {selectedPeriod === "Quarterly" && showDropdown.quarter && (
                      <Dropdown
                        items={quarterlyOptions}
                        onSelect={(val) => {
                          setSelectedQuarter(val);
                          setShowDropdown((prev) => ({
                            ...prev,
                            quarter: false,
                          }));
                        }}
                        position="center"
                      />
                    )}
                  </div>

                  {/* Month Button */}
                  <div className="relative">
                    <button
                      onClick={() => handlePeriodClick("Monthly")}
                      className="px-3 py-0.5 h-7 text-sm rounded-lg border border-[#458276] bg-white text-gray-700 hover:bg-[#06513D] hover:text-white transition whitespace-nowrap"
                    >
                      {selectedMonth || "Month"}
                    </button>
                    {selectedPeriod === "Monthly" && showDropdown.month && (
                      <Dropdown
                        items={monthlyOptions}
                        onSelect={(val) => {
                          setSelectedMonth(val);
                          setShowDropdown((prev) => ({
                            ...prev,
                            month: false,
                          }));
                        }}
                        shorten={true}
                        position="center"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
